import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const PIN_POPOVER_WIDTH = 292;
const VIEWPORT_GAP = 12;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export default function AdminEmisorPin({ adminName = '' }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);
  const nombre = String(adminName || 'el administrativo').trim();

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const popover = popoverRef.current;
    if (!anchor || !popover) return;

    const rect = anchor.getBoundingClientRect();
    const popRect = popover.getBoundingClientRect();
    const popW = popRect.width || PIN_POPOVER_WIDTH;
    const popH = popRect.height || 0;

    let left = rect.right - popW;
    left = clamp(left, VIEWPORT_GAP, window.innerWidth - popW - VIEWPORT_GAP);

    let top = rect.top - popH - 10;
    let placement = 'above';
    if (top < VIEWPORT_GAP) {
      top = rect.bottom + 10;
      placement = 'below';
    }

    setCoords({ left, top, placement });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return undefined;
    }
    updatePosition();
    const raf = window.requestAnimationFrame(updatePosition);
    return () => window.cancelAnimationFrame(raf);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handleReposition = () => updatePosition();
    const handlePointerDown = (event) => {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (anchor?.contains(event.target) || popover?.contains(event.target)) return;
      setOpen(false);
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open, updatePosition]);

  const popover = open && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={popoverRef}
        className={`crm-re-admin-pin__pop crm-re-admin-pin__pop--portal${coords?.placement === 'below' ? ' crm-re-admin-pin__pop--below' : ''}`}
        style={coords ? { left: coords.left, top: coords.top } : { visibility: 'hidden' }}
        role="tooltip"
      >
        <span className="crm-re-admin-pin__pop-title">Tu turno de emision</span>
        <span className="crm-re-admin-pin__pop-text">
          Este tratamiento debe ser emitido por <strong>{nombre}</strong> desde Recetas a emitir.
        </span>
      </div>,
      document.body
    )
    : null;

  return (
    <>
      <span
        ref={anchorRef}
        className={`crm-re-admin-pin${open ? ' crm-re-admin-pin--open' : ''}`}
        tabIndex={0}
        role="button"
        aria-expanded={open}
        aria-label={`Atencion: ${nombre} debe emitir estas recetas`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <span className="crm-re-admin-pin__icon" aria-hidden="true">📌</span>
      </span>
      {popover}
    </>
  );
}
